import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Scale, Trash2, ArrowRight } from "lucide-react";
import { useCompare } from "../lib/compare";
import { getProduct } from "../lib/api";

/**
 * Floating "compare tray" + full-screen comparison modal.
 *
 * The tray appears at the bottom-right whenever 1+ products have been added.
 * Tapping "Compare" opens the modal which loads each product (fresh, by id)
 * and renders a side-by-side spec table with photo, brand, retail-from price,
 * colour count, size range and a "Customize" CTA per garment.
 */
export default function CompareTray() {
  const { ids, count, max, full, remove, clear } = useCompare();
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState({});  // id -> product
  const [loading, setLoading] = useState(false);

  // Load product details on demand (when tray opens the modal)
  useEffect(() => {
    if (!open || ids.length === 0) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(ids.map((id) => getProduct(id).catch(() => null)))
      .then((rows) => {
        if (cancelled) return;
        const map = {};
        for (const p of rows) if (p && p.id) map[p.id] = p;
        setProducts(map);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, ids]);

  if (count === 0) return null;

  return (
    <>
      {/* Floating tray */}
      <div
        className="fixed bottom-4 right-4 z-40 surface-card border-[var(--tc-red)] bg-black/90 backdrop-blur p-3 flex items-center gap-3 shadow-2xl"
        data-testid="compare-tray"
      >
        <Scale size={18} className="text-[var(--tc-red)]" />
        <span className="font-mono text-[10px] tracking-widest text-zinc-400 hidden sm:inline">COMPARE</span>
        <span
          className="font-ui font-bold text-sm"
          data-testid="compare-count"
        >{count} / {max}</span>
        <button
          onClick={() => setOpen(true)}
          disabled={count < 2}
          data-testid="compare-open-btn"
          className="btn-tc-primary py-2 px-4 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {count < 2 ? "Add 1 more" : "Compare"}
        </button>
        <button
          onClick={clear}
          data-testid="compare-clear-btn"
          className="text-zinc-500 hover:text-white"
          title="Clear all"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Side-by-side modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/90 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="compare-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="min-h-screen p-4 lg:p-10 max-w-[1400px] mx-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono text-[11px] tracking-widest text-zinc-500">/ COMPARE_GARMENTS</div>
                <h2 className="font-display text-4xl md:text-5xl uppercase mt-1">Side-by-Side</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="compare-close-btn"
                className="w-10 h-10 border border-[#222] hover:border-white flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>

            {loading && <div className="text-zinc-500 font-mono">Loading…</div>}

            {!loading && (
              <div className={`grid gap-4 ${count === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                {ids.map((id) => {
                  const p = products[id];
                  if (!p) {
                    return (
                      <div key={id} className="surface-card p-6 text-zinc-500 font-mono text-sm">
                        Product not found
                        <button onClick={() => remove(id)} className="block mt-2 text-[var(--tc-red)] underline text-xs">Remove</button>
                      </div>
                    );
                  }
                  const sizeMin = p.sizes?.[0];
                  const sizeMax = p.sizes?.[p.sizes.length - 1];
                  return (
                    <div key={id} className="surface-card overflow-hidden" data-testid={`compare-col-${id}`}>
                      <div className="aspect-[4/5] bg-[#080808] relative">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                        <button
                          onClick={() => remove(id)}
                          data-testid={`compare-remove-${id}`}
                          className="absolute top-3 right-3 w-8 h-8 bg-black/80 border border-[#222] hover:border-[var(--tc-red)] flex items-center justify-center"
                          title="Remove from compare"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="p-5 space-y-3">
                        {p.brand_line && (
                          <div className="font-mono text-[10px] tracking-widest text-zinc-500 truncate">
                            {p.brand_line.toUpperCase()}{p.sku ? ` · ${p.sku}` : ""}
                          </div>
                        )}
                        <div className="font-ui font-bold uppercase tracking-wider text-base">{p.name}</div>
                        <p className="text-zinc-400 text-xs font-body line-clamp-3">{p.description}</p>

                        <div className="border-t border-[#222] pt-3 space-y-2 font-mono text-[11px]">
                          <Spec label="RETAIL FROM" value={`$${p.base_price?.toFixed(2)}`} hi />
                          <Spec label="BRAND" value={p.brand || "—"} />
                          <Spec label="CATEGORY" value={(p.category || "—").toUpperCase()} />
                          <Spec label="COLOURS" value={`${(p.colors || []).length}`} />
                          <Spec label="SIZES" value={p.sizes?.length ? `${sizeMin} – ${sizeMax}` : "—"} />
                          <Spec label="BACK PRINT" value={Object.keys(p.back_color_images || {}).length > 0 ? "YES" : "—"} />
                        </div>

                        <Link
                          to={`/customize/${p.id}`}
                          onClick={() => setOpen(false)}
                          data-testid={`compare-customize-${p.id}`}
                          className="btn-tc-primary w-full mt-2 flex items-center justify-center gap-2 py-3 text-xs"
                        >
                          Customize <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Color swatches comparison strip */}
            {!loading && count >= 2 && (
              <div className="mt-8 surface-card p-5" data-testid="compare-colours-row">
                <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-4">/ COLOUR_AVAILABILITY</div>
                <div className="space-y-4">
                  {ids.map((id) => {
                    const p = products[id];
                    if (!p) return null;
                    return (
                      <div key={id} className="flex items-start gap-4">
                        <div className="font-ui font-bold text-xs uppercase tracking-wider min-w-[120px] mt-1 truncate">{p.name}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {(p.colors || []).map((c) => (
                            <span
                              key={c}
                              className="w-5 h-5 border border-[#222]"
                              style={{ background: c }}
                              title={p.color_names?.[c] || c}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Spec({ label, value, hi }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className="text-zinc-500 tracking-widest">{label}</span>
      <span className={`${hi ? "text-[var(--tc-red)] text-lg font-display" : "text-zinc-200"} text-right`}>
        {value}
      </span>
    </div>
  );
}
